/* Gleva · Direction B · gvb.js — shared behaviour (BUILD-SPEC §5), ported from final-b/_src/shared.js.
   ES module, deferred, loaded once by layout/theme.liquid. Other modules: `import { G } from 'gvb';` (importmap)
   or read window.GVB. API and events: _build/foundation/NOTES.md.
   Prices in this file are in PAISE (Shopify cents) unless a name says rupees: G.money(54900) === '₹549'. */

const $ = (s, r) => (r || document).querySelector(s);
const $$ = (s, r) => Array.from((r || document).querySelectorAll(s));
const reduceMq = window.matchMedia('(prefers-reduced-motion: reduce)');

const readJSON = (id) => {
  try { return JSON.parse(document.getElementById(id)?.textContent || '{}'); } catch (e) { return {}; }
};
const store = Object.assign({ free_threshold: 89000, after_add: 'toast' }, readJSON('gvb-store'));
const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

/* ₹ + whole rupees + en-IN grouping, identical to snippets/gvb-price.liquid (₹1,199 · ₹1,00,000) */
const group = (n) => {
  let s = String(Math.round(Math.abs(Number(n) || 0)));
  if (s.length <= 3) return s;
  const last = s.slice(-3), parts = [];
  let head = s.slice(0, -3);
  while (head.length > 2) { parts.unshift(head.slice(-2)); head = head.slice(0, -2); }
  if (head) parts.unshift(head);
  return parts.join(',') + ',' + last;
};
const fmt = (rupees) => '₹' + group(rupees);
const money = (cents) => fmt(Math.round((Number(cents) || 0) / 100));

export const G = {
  $, $$, store, fmt, money,
  get reduce() { return reduceMq.matches; }
};

/* prices change instantly: no count-up, no animation (a price is a fact, not a show) */
G.setPrice = (els, cents) => {
  const list = !els ? [] : (els.nodeType ? [els] : Array.from(els));
  list.forEach((el) => { el.textContent = money(cents); });
};

/* "✓ Added" for 1.6s */
G.flashAdded = (btn) => {
  if (!btn) return;
  btn.classList.add('is-added');
  clearTimeout(btn._gvbT);
  btn._gvbT = setTimeout(() => btn.classList.remove('is-added'), 1600);
};

/* ---------- radiogroups: click + arrow keys + roving tabindex ---------- */
const select = (radios, r) => {
  radios.forEach((x) => { const on = x === r; x.setAttribute('aria-checked', on ? 'true' : 'false'); x.tabIndex = on ? 0 : -1; });
};
const arrowTarget = (e, radios, k) => {
  const n = radios.length;
  if (e.key === 'ArrowRight' || e.key === 'ArrowDown') return radios[(k + 1) % n];
  if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') return radios[(k - 1 + n) % n];
  return null;
};
/* explicit binding (section code): returns sel(radio, silent) */
G.radiogroup = (groupEl, onChange) => {
  groupEl._gvbRg = true;
  const radios = $$('[role="radio"]', groupEl);
  const sel = (r, silent) => { select(radios, r); if (!silent && onChange) onChange(r); };
  radios.forEach((r, k) => {
    r.tabIndex = r.getAttribute('aria-checked') === 'true' ? 0 : -1;
    r.addEventListener('click', () => sel(r));
    r.addEventListener('keydown', (e) => {
      const t = arrowTarget(e, radios, k);
      if (t) { e.preventDefault(); sel(t); t.focus(); }
    });
  });
  return sel;
};

/* ---------- products ([data-gvb-product], e.g. gvb-card): options drive price, label, variant id, sold-out ---------- */
const mine = (p) => (el) => el.closest('[data-gvb-product]') === p;
const applyOption = (r) => {
  const p = r.closest('[data-gvb-product]');
  if (!p) return;
  const own = mine(p), d = r.dataset;
  if (d.price) { p.dataset.price = d.price; G.setPrice($$('[data-price-label]', p).filter(own), d.price); }
  if (d.variant) { p.dataset.variant = d.variant; $$('[data-variant-label]', p).filter(own).forEach((el) => { el.textContent = d.variant; }); }
  if (d.variantId) { $$('input[name="id"]', p).filter(own).forEach((i) => { i.value = d.variantId; }); }
  const out = r.hasAttribute('data-soldout');
  $$('[data-soldout-label]', p).filter(own).forEach((el) => { el.hidden = !out; });
  $$('[data-gvb-add]', p).filter(own).forEach((b) => { b.disabled = out; b.classList.toggle('is-soldout', out); });
  p.dispatchEvent(new CustomEvent('gvb:option-change', { bubbles: true, detail: { variantId: d.variantId, price: Number(d.price), variant: d.variant, soldout: out } }));
};
const delegatedGroup = (el) => {
  const g = el && el.closest('[role="radiogroup"]');
  return g && !g._gvbRg && !g.hasAttribute('data-custom') && g.closest('[data-gvb-product]') ? g : null;
};
document.addEventListener('click', (e) => {
  const r = e.target.closest && e.target.closest('[role="radio"]');
  const g = delegatedGroup(r);
  if (!g) return;
  select($$('[role="radio"]', g), r);
  applyOption(r);
});
document.addEventListener('keydown', (e) => {
  const r = e.target.closest && e.target.closest('[role="radio"]');
  const g = delegatedGroup(r);
  if (!g) return;
  const radios = $$('[role="radio"]', g), t = arrowTarget(e, radios, radios.indexOf(r));
  if (!t) return;
  e.preventDefault();
  select(radios, t);
  t.focus();
  applyOption(t);
});

/* ---------- rails: progress line + optional arrows ---------- */
const railUpdate = (r) => {
  const prog = r.nextElementSibling && r.nextElementSibling.classList.contains('rail-progress') ? r.nextElementSibling : null;
  const bar = prog ? $('i', prog) : null;
  const max = r.scrollWidth - r.clientWidth;
  if (bar) {
    const w = Math.max(12, Math.min(100, r.clientWidth / (r.scrollWidth || 1) * 100));
    const p = max > 0 ? r.scrollLeft / max : 0;
    bar.style.setProperty('--w', w + '%');
    bar.style.setProperty('--x', (p * (100 - w) / w * 100) + '%');
    prog.style.visibility = max > 4 ? 'visible' : 'hidden';
  }
  const sec = r.closest('section, .shopify-section');
  if (sec) {
    const prev = $('[data-rail-prev]', sec), next = $('[data-rail-next]', sec);
    if (prev) prev.disabled = r.scrollLeft < 4;
    if (next) next.disabled = r.scrollLeft > max - 4;
  }
};
G.updateRails = (scope) => $$('[data-rail]', scope).forEach(railUpdate);
document.addEventListener('scroll', (e) => {
  const r = e.target;
  if (r && r.nodeType === 1 && r.hasAttribute('data-rail')) requestAnimationFrame(() => railUpdate(r));
}, { capture: true, passive: true });
window.addEventListener('resize', () => G.updateRails());
document.addEventListener('click', (e) => {
  const btn = e.target.closest && e.target.closest('[data-rail-prev], [data-rail-next]');
  if (!btn) return;
  const sec = btn.closest('section, .shopify-section'), r = sec && $('[data-rail]', sec);
  if (!r) return;
  const item = r.firstElementChild;
  const w = item ? item.getBoundingClientRect().width + 16 : r.clientWidth * 0.8;
  r.scrollBy({ left: (btn.hasAttribute('data-rail-prev') ? -1 : 1) * w, behavior: G.reduce ? 'auto' : 'smooth' });
});

/* ---------- header hairline once the page moves ---------- */
let stuckTick = false;
const onScroll = () => {
  stuckTick = false;
  const on = window.scrollY > 8;
  $$('.hdr, [data-gvb-header]').forEach((h) => h.classList.toggle('is-stuck', on));
};
window.addEventListener('scroll', () => { if (!stuckTick) { stuckTick = true; requestAnimationFrame(onScroll); } }, { passive: true });

/* ---------- bag badge ([data-gvb-bag-count]; Impact's <cart-count> updates itself) ---------- */
G.setBagCount = (count, bump) => {
  $$('[data-gvb-bag-count]').forEach((b) => {
    b.textContent = count;
    b.hidden = !count;
    if (bump && count) { b.classList.remove('bump'); void b.offsetWidth; b.classList.add('bump'); }
  });
};
document.addEventListener('cart:change', (e) => {
  const cart = e.detail && e.detail.cart;
  if (cart && typeof cart.item_count === 'number') G.setBagCount(cart.item_count, e.detail.baseEvent === 'variant:add');
});
document.addEventListener('cart:refresh', async () => {
  try { const cart = await (await fetch(root() + 'cart.js')).json(); G.setBagCount(cart.item_count, false); } catch (e) { /* offline: keep the badge */ }
});

/* ---------- toast ---------- */
let toastTimer;
const toastEl = () => {
  let t = document.getElementById('gvb-toast');
  if (t) return t;
  t = document.createElement('div');
  t.id = 'gvb-toast';
  t.className = 'toast gvb';
  t.setAttribute('role', 'status');
  t.innerHTML = '<span class="toast-thumb"><img alt="" width="48" height="48" decoding="async" hidden></span>'
    + '<span class="toast-text"><span class="t-micro">Added to bag</span><b class="toast-name"></b><span class="toast-meta num"></span></span>'
    + '<a class="btn btn-paper on-ink" href="' + root() + 'cart" data-gvb-view-bag>View bag</a>';
  document.body.appendChild(t);
  $('[data-gvb-view-bag]', t).addEventListener('click', (ev) => {
    const drawer = document.getElementById('cart-drawer') || document.querySelector('cart-drawer');
    if (drawer && typeof drawer.show === 'function') {
      ev.preventDefault();
      t.classList.remove('is-on');
      drawer.show();
    }
  });
  return t;
};
G.toast = (o) => {
  const t = toastEl();
  $('.toast-name', t).textContent = o.name || '';
  $('.toast-meta', t).textContent = (o.variant ? o.variant + ' · ' : '') + (o.price != null ? money(o.price) : '');
  const img = $('.toast-thumb img', t);
  if (o.thumb) { img.src = o.thumb; img.hidden = false; } else { img.hidden = true; }
  t.classList.add('is-on');
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => t.classList.remove('is-on'), 2600);
};

/* ---------- after an add: drawer or toast, "Added", announcement (BUILD-SPEC §2.4, §5) ---------- */
const announce = (msg) => {
  const live = document.getElementById('status-announcement');
  if (live) { live.textContent = ''; requestAnimationFrame(() => { live.textContent = msg; }); }
};
const thumbUrl = (u) => (u ? u + (u.indexOf('?') < 0 ? '?' : '&') + 'width=160' : '');
const fromItems = (items) => {
  if (!items || !items.length) return null;
  const a = items[0];
  const variant = a.variant_title && a.variant_title !== 'Default Title' ? a.variant_title : '';
  if (items.length === 1) return { name: a.product_title || a.title, variant, price: a.final_line_price != null ? a.final_line_price : a.price, thumb: thumbUrl(a.image) };
  const total = items.reduce((s, x) => s + (x.final_line_price != null ? x.final_line_price : x.price * (x.quantity || 1)), 0);
  return { name: (a.product_title || a.title) + ' + ' + (items.length - 1) + ' more', variant: '', price: total, thumb: thumbUrl(a.image) };
};
/* capture phase, before Impact's cart drawer listener: toast mode keeps the shopper on the page */
window.addEventListener('variant:add', (e) => {
  if (store.after_add === 'toast' && e.detail) e.detail.blockCartDrawerOpening = true;
}, true);
document.addEventListener('variant:add', (e) => {
  const d = e.detail || {};
  const p = e.target && e.target.closest ? e.target.closest('[data-gvb-product]') : null;
  if (p) $$('[data-gvb-add]', p).filter(mine(p)).forEach(G.flashAdded);
  let o = d.gvbToast || G.nextToast || null;
  G.nextToast = null;
  if (!o && p) {
    const item = d.items && d.items[0];
    o = { name: p.dataset.name, variant: p.dataset.variant || '', price: item ? (item.final_line_price != null ? item.final_line_price : item.price) : Number(p.dataset.price), thumb: p.dataset.thumb };
  }
  if (!o) o = fromItems(d.items);
  if (!o) return;
  o.count = d.cart && d.cart.item_count;
  document.dispatchEvent(new CustomEvent('gvb:added', { detail: o }));
});
document.addEventListener('gvb:added', (e) => {
  const o = e.detail || {};
  if (store.after_add === 'toast') G.toast(o);
  if (typeof o.count === 'number') {
    announce((o.name || 'Item') + (o.variant ? ', ' + o.variant : '') + ' added to bag. ' + o.count + (o.count === 1 ? ' item' : ' items') + ' in your bag.');
  }
});

/* ---------- add without a product form (home kit, bundles): mirrors Impact's ProductForm submit ---------- */
G.cartAdd = async (items, meta) => {
  const sections = ['variant-added'];
  document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));
  const res = await fetch(root() + 'cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    body: JSON.stringify({ items, sections: sections.join(','), sections_url: root() + 'variants/' + items[0].id })
  });
  const json = await res.json();
  if (!res.ok) {
    document.dispatchEvent(new CustomEvent('cart:error', { detail: { error: json.description } }));
    document.dispatchEvent(new CustomEvent('cart:refresh'));
    throw new Error(json.description || 'Could not add to bag');
  }
  const cart = await (await fetch(root() + 'cart.js')).json();
  cart.sections = json.sections;
  const src = (meta && meta.source) || document.documentElement;
  const detail = { items: json.items || [json], cart };
  if (meta && meta.name) detail.gvbToast = { name: meta.name, variant: meta.variant || '', price: meta.price, thumb: meta.thumb };
  src.dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail }));
  document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart } }));
  return cart;
};

/* ---------- free-delivery strip in gvb-assurance (per selected tier/variant price, not the cart) ---------- */
G.updateAssurance = (cents, scope) => {
  $$('[data-gvb-assure]', scope).forEach((box) => {
    const th = Number(box.dataset.threshold) || store.free_threshold;
    const gap = th - cents;
    const txt = $('[data-free-text]', box);
    if (txt) {
      if (gap > 0) txt.innerHTML = 'Add <b class="num" data-free-gap>' + money(gap) + '</b> more for free delivery';
      else txt.textContent = box.dataset.metText || 'This set gets free delivery';
    }
    const fill = $('[data-free-fill]', box);
    if (fill) fill.style.setProperty('--p', Math.max(0, Math.min(1, cents / th)).toFixed(3));
  });
};

/* ---------- init (page load + theme editor) ---------- */
G.init = (scope) => { G.updateRails(scope); onScroll(); };
document.addEventListener('shopify:section:load', (e) => G.init(e.target));
G.init(document);

window.GVB = G;
if (!('G' in window)) window.G = G;
export default G;
